import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/24/outline';
import type { TreatmentCptAnalysis, CodeSuggestion } from '../../types/codingAssistant';
import {
  getTreatmentCptAnalyses,
  searchCptCodes,
  reviewTreatmentCptCode
} from '../../services/codingAssistant';
import { isSubscriptionError } from '../../services/api';
import { UnifiedCodeList } from './UnifiedCodeCard';

interface Props {
  appointmentId?: string;
  encounterId?: string;
  isEncounterMode?: boolean;
  /** Treatment notes from physician (e.g., "ekg, flu shot") to show as search suggestions */
  treatmentNotes?: string;
}

export const TreatmentCptCodesSection: React.FC<Props> = ({ appointmentId, encounterId, isEncounterMode = false, treatmentNotes = '' }) => {
  const [analyses, setAnalyses] = useState<TreatmentCptAnalysis[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CodeSuggestion[]>([]);

  // Parse treatment notes into individual search suggestions
  // Split by common delimiters: comma, semicolon, newline, "and"
  const treatmentSuggestions = useMemo(() => {
    if (!treatmentNotes.trim()) return [];
    return treatmentNotes
      .split(/[,;\n]+|\s+and\s+/i)
      .map(s => s.trim())
      .filter(s => s.length > 1); // Filter out single characters
  }, [treatmentNotes]);

  const loadTreatmentCptAnalyses = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getTreatmentCptAnalyses(appointmentId, encounterId, isEncounterMode);

      // Handle response structure - load approved codes from database
      if (response && response.data) {
        setAnalyses(response.data.analyses || []);
      } else {
        setAnalyses([]);
      }
    } catch (error) {
      if (!isSubscriptionError(error)) {
        console.error('Failed to load treatment CPT analyses', error);
      }
      setAnalyses([]);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, encounterId, isEncounterMode]);

  useEffect(() => {
    loadTreatmentCptAnalyses();
  }, [loadTreatmentCptAnalyses]);

  const handleSearchCodes = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setIsSearching(true);
      const response = await searchCptCodes(appointmentId, encounterId, isEncounterMode, searchQuery);
      const codes = response.data.codes;

      if (codes.length > 0) {
        setSearchResults(codes);
        toast.success(`Found ${codes.length} CPT codes`);
      } else {
        setSearchResults([]);
        toast.error('No codes found. Try a different search term.');
      }
    } catch (error: any) {
      if (!isSubscriptionError(error)) {
        const errorMsg = error?.response?.data?.error || 'Failed to search CPT codes';
        toast.error(errorMsg);
      }
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleApproveCode = async (code: CodeSuggestion) => {
    try {
      await reviewTreatmentCptCode(
        appointmentId,
        encounterId,
        isEncounterMode,
        code,
        'approved',
        '', // No specific treatment text for manual search
        'manual-search'
      );

      // Add to analyses (create a new analysis if needed)
      const existingAnalysis = analyses.find(a => a.sourceSection === 'manual-search');

      if (existingAnalysis) {
        setAnalyses(prevAnalyses =>
          prevAnalyses.map(a => {
            if (a.sourceSection === 'manual-search') {
              return {
                ...a,
                suggestedCodes: [...a.suggestedCodes, { ...code, status: 'approved' as const }]
              };
            }
            return a;
          })
        );
      } else {
        setAnalyses(prevAnalyses => [
          ...prevAnalyses,
          {
            id: 'manual-search',
            treatmentText: 'Manually searched procedures',
            sourceSection: 'manual-search',
            analyzedAt: new Date().toISOString(),
            suggestedCodes: [{ ...code, status: 'approved' as const }]
          }
        ]);
      }

      // Remove from search results
      setSearchResults(codes => codes.filter(c => c.code !== code.code));
      toast.success(`CPT code ${code.code} approved and saved`);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        toast.error('Failed to approve code');
      }
    }
  };

  const handleDismissCode = async (code: CodeSuggestion) => {
    // Just remove from search results, don't save rejection
    setSearchResults(codes => codes.filter(c => c.code !== code.code));
    toast.success(`CPT code ${code.code} dismissed`);
  };

  const handleRemoveApprovedCode = async (code: CodeSuggestion) => {
    if (!confirm(`Remove approved code ${code.code}?`)) return;

    try {
      await reviewTreatmentCptCode(
        appointmentId,
        encounterId,
        isEncounterMode,
        code,
        'rejected',
        '',
        'manual-search'
      );

      // Remove from analyses
      setAnalyses(prevAnalyses =>
        prevAnalyses.map(a => {
          if (a.sourceSection === 'manual-search') {
            const updatedCodes = a.suggestedCodes.filter(c => c.code !== code.code);
            return {
              ...a,
              suggestedCodes: updatedCodes
            };
          }
          return a;
        }).filter(a => a.sourceSection !== 'manual-search' || a.suggestedCodes.length > 0)
      );

      toast.success(`CPT code ${code.code} removed`);
    } catch (error) {
      if (!isSubscriptionError(error)) {
        toast.error('Failed to remove code');
      }
    }
  };

  if (isLoading && analyses.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Get approved codes for display
  const approvedCodes = analyses
    .filter(a => a.sourceSection === 'manual-search')
    .flatMap(a => a.suggestedCodes.filter(c => c.status === 'approved'));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">Treatment Procedures (CPT Codes)</h3>
          <p className="text-sm text-gray-600 mt-1">
            Search and select CPT codes for procedures performed during the visit.
          </p>
        </div>
      </div>

      {/* Treatment Notes Suggestions */}
      {treatmentSuggestions.length > 0 && (
        <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
          <div className="flex items-center gap-2 mb-2">
            <SparklesIcon className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-semibold text-purple-800">From Treatment Notes</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {treatmentSuggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setSearchQuery(suggestion);
                  // Auto-trigger search
                  setTimeout(() => {
                    const searchBtn = document.getElementById('cpt-search-btn');
                    if (searchBtn) searchBtn.click();
                  }, 100);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-white border border-purple-300 rounded-full hover:bg-purple-100 hover:border-purple-400 transition-colors"
              >
                <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CPT Code Search */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h5 className="text-sm font-semibold text-gray-900 mb-3">
          Search CPT Codes
        </h5>

        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchCodes()}
            placeholder="Search by procedure (e.g., 'office visit', 'injection', 'X-ray')"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            id="cpt-search-btn"
            onClick={handleSearchCodes}
            disabled={isSearching || !searchQuery.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
            {isSearching ? 'Searching...' : 'Search CPT'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4">
            <h6 className="text-sm font-semibold text-gray-700 mb-2">
              Search Results ({searchResults.length})
            </h6>
            <UnifiedCodeList
              codes={searchResults.map(code => ({
                code: code.code,
                description: code.description,
                confidence: code.confidence,
                source: code.source as 'imo' | 'imo-precision' | 'llm' | 'ai',
                category: code.category,
                rationale: code.llmRationale,
                status: 'suggested' as const,
                codeType: 'cpt' as const
              }))}
              onApprove={(codeStr) => {
                const code = searchResults.find(c => c.code === codeStr);
                if (code) handleApproveCode(code);
              }}
              onReject={(codeStr) => {
                const code = searchResults.find(c => c.code === codeStr);
                if (code) handleDismissCode(code);
              }}
              showApprovedFirst={false}
              emptyMessage="No codes found"
            />
          </div>
        )}
      </div>

      {/* Approved CPT Codes */}
      {approvedCodes.length > 0 && (
        <div className="mt-4">
          <UnifiedCodeList
            codes={approvedCodes.map(code => ({
              code: code.code,
              description: code.description,
              confidence: code.confidence,
              source: code.source as 'imo' | 'imo-precision' | 'llm' | 'ai',
              category: code.category,
              rationale: code.llmRationale,
              status: 'approved' as const,
              codeType: 'cpt' as const
            }))}
            onRemove={(codeStr) => {
              const code = approvedCodes.find(c => c.code === codeStr);
              if (code) handleRemoveApprovedCode(code);
            }}
            showApprovedFirst={true}
            emptyMessage="No approved codes"
          />
        </div>
      )}

      {!isSearching && searchResults.length === 0 && approvedCodes.length === 0 && !searchQuery && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-600 font-medium">No CPT codes selected yet</p>
          <p className="text-sm text-gray-500 mt-2">
            Search for procedures to add CPT codes to this visit.
          </p>
        </div>
      )}
    </div>
  );
};
